import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * MFA is disabled platform-wide (Phase M5.x) — the ProtectedRoute no longer
 * enforces MFA setup and the MfaLockScreen is not rendered. Kept minimal so
 * navigation goes straight into the requested page as soon as the user is
 * authenticated.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { status, activeMembership, me } = useAuth();
    const location = useLocation();

    if (status === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
                <Loader2 className="animate-spin" size={24} style={{ color: "var(--text-muted)" }} />
            </div>
        );
    }
    if (status === "anonymous" || !me) return <Navigate to="/login" replace state={{ from: location }} />;
    if (activeMembership?.role?.code === "parent" && !location.pathname.startsWith("/portal")) {
        return <Navigate to="/portal" replace />;
    }
    if (
        activeMembership &&
        !activeMembership.tenant.onboarding_completed &&
        activeMembership.role?.code === "institution_owner" &&
        !location.pathname.startsWith("/onboarding")
    ) {
        return <Navigate to="/onboarding" replace />;
    }
    return <>{children}</>;
}

export function PortalRoute({ children }: { children: React.ReactNode }) {
    const { status, activeMembership, me } = useAuth();
    const location = useLocation();
    if (status === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
                <Loader2 className="animate-spin" size={24} style={{ color: "var(--text-muted)" }} />
            </div>
        );
    }
    if (status === "anonymous" || !me) return <Navigate to="/login" replace state={{ from: location }} />;
    if (activeMembership?.role?.code !== "parent") return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
}

export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
    const { status, activeMembership } = useAuth();
    if (status === "loading") return null;
    if (status === "authenticated") {
        const dest = activeMembership?.role?.code === "parent" ? "/portal" : "/dashboard";
        return <Navigate to={dest} replace />;
    }
    return <>{children}</>;
}
